// AUTO.2 PR2 (#1404 follow-on) — QA test-execution language card for the
// project dashboard.
//
// Completes the settings half of the per-project `language` column: no
// auto-detection (a fixed default would silently run the wrong test
// command and produce a confidently-wrong verdict), so the project owner
// must set it explicitly. Modeled on DeliveryModeSettingsCard's basic
// shape (read-only current value + "Change" → inline editor → Apply →
// inline error slot → onChange refresh) but without any tier/phase
// gating — the value only picks which shell command the QA sub-agent
// runs, so there's no billing or state-machine concern to guard; the
// control is always editable.

import { useState } from 'react';
import { apiFetch } from '../lib/api';

type QaLanguage = 'typescript' | 'python' | 'go';

const LANGUAGE_OPTIONS: Array<{ value: QaLanguage; label: string; helper: string }> = [
  { value: 'typescript', label: 'TypeScript / JavaScript', helper: 'Runs `npm test`.' },
  { value: 'python',     label: 'Python',                  helper: 'Runs `pytest`.' },
  { value: 'go',         label: 'Go',                      helper: 'Runs `go test ./...`.' },
];

interface QaLanguageSettingsCardProps {
  projectId: string;
  /** Current value stored on `projects.language`. */
  currentLanguage: QaLanguage;
  /** Called with the freshly-applied language so the parent can refresh. */
  onChange?: (next: QaLanguage) => void;
}

export default function QaLanguageSettingsCard({
  projectId, currentLanguage, onChange,
}: QaLanguageSettingsCardProps) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<QaLanguage>(currentLanguage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (pending === currentLanguage) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await apiFetch<{ language: QaLanguage }>(
        `/projects/${projectId}`,
        { method: 'PATCH', body: JSON.stringify({ language: pending }) },
      );
      onChange?.(updated.language);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change QA language');
    } finally {
      setBusy(false);
    }
  };

  const currentLabel = LANGUAGE_OPTIONS.find(o => o.value === currentLanguage)?.label ?? currentLanguage;

  return (
    <div
      data-testid="qa-language-settings-card"
      style={{
        padding: '16px 18px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
          QA Test-Execution Language
        </h3>
        {!editing && (
          <button
            data-testid="qa-language-change-button"
            onClick={() => { setPending(currentLanguage); setEditing(true); setError(null); }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--primary)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Change
          </button>
        )}
      </div>

      {!editing && (
        <div data-testid="qa-language-current" style={{ marginTop: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--text)', margin: 0 }}>{currentLabel}</p>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            The language the QA Agent runs against — set this to what the project actually is. No auto-detection.
          </p>
        </div>
      )}

      {editing && (
        <div data-testid="qa-language-editor" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div role="radiogroup" aria-label="QA Test-Execution Language" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {LANGUAGE_OPTIONS.map(opt => {
              const selected = pending === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`qa-language-option-${opt.value}`}
                  onClick={() => setPending(opt.value)}
                  disabled={busy}
                  style={{
                    textAlign:    'left',
                    padding:      12,
                    borderRadius: 'var(--radius-sm)',
                    border:       selected ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background:   selected ? 'rgba(124,58,237,0.06)' : 'var(--surface)',
                    cursor:       busy ? 'default' : 'pointer',
                    outline:      'none',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                    {opt.label}
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {opt.helper}
                  </div>
                </button>
              );
            })}
          </div>

          {error && (
            <p data-testid="qa-language-error" style={{ fontSize: 12, color: 'var(--error)', margin: 0 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              data-testid="qa-language-apply"
              onClick={apply}
              disabled={busy || pending === currentLanguage}
              style={{
                padding: '6px 14px',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                fontWeight: 600,
                cursor: busy || pending === currentLanguage ? 'default' : 'pointer',
                opacity: busy || pending === currentLanguage ? 0.6 : 1,
              }}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
            <button
              data-testid="qa-language-cancel"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={busy}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
